import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register, organizationSignup, clearError, clearOtpState, clearEmailOtpState, clearRegistrationSuccess, sendOtp, verifyOtp, resendOtp, sendEmailOtp, verifyEmailOtp, resendEmailOtp } from '../../store/slices/authSlice';
import registrationService from '../../services/registrationService';
import { parseJwtSubject } from '../../utils/tabSessionUtils';
import { AuthBrandingPanel, AuthMobileHeader } from './AuthBrandingPanel';
import VerifyAccountModal from './VerifyAccountModal';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_REGEX = /^[a-zA-Z0-9._-]{3,20}$/;
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const MIN_AGE_YEARS = 10;
const DEBOUNCE_MS = 500;

const getMaxDobForMinAge = (minAge) => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - minAge);
  return date.toISOString().split('T')[0];
};

const LABEL_CLASS = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';
const inputClass = (hasError) =>
  `w-full px-4 py-3 border ${hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all`;

const PasswordToggle = ({ show, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
    aria-label={show ? 'Hide password' : 'Show password'}
    tabIndex={-1}
  >
    {show ? (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path d="M22 12C22 12 19 18 12 18C5 18 2 12 2 12C2 12 5 6 12 6C19 6 22 12 22 12Z" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3" />
        <path d="M3 21L20 4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path d="M12 5C8.24261 5 5.43602 7.4404 3.76737 9.43934C2.51521 10.9394 2.51521 13.0606 3.76737 14.5607C5.43602 16.5596 8.24261 19 12 19C15.7574 19 18.564 16.5596 20.2326 14.5607C21.4848 13.0606 21.4848 10.9394 20.2326 9.43934C18.564 7.4404 15.7574 5 12 5Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const Register = () => {
  const [accountType, setAccountType] = useState('individual');
  const [currentStep, setCurrentStep] = useState(1);
  const [hasCustomSlug, setHasCustomSlug] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    organizationSlug: '',
    mobile: '',
    otp: '',
    emailOtp: '',
  });
  const [organizationFormData, setOrganizationFormData] = useState({
    organizationName: '',
    organizationSlug: '',
    ownerEmail: '',
    ownerUsername: '',
    ownerPassword: '',
    confirmOwnerPassword: '',
    ownerMobile: '',
    ownerOtp: '',
    ownerEmailOtp: '',
  });
  
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  const [usernameAvailability, setUsernameAvailability] = useState('idle');
  const [usernameCheckedValue, setUsernameCheckedValue] = useState('');
  const [usernameCheckLoading, setUsernameCheckLoading] = useState(false);
  const [emailAvailability, setEmailAvailability] = useState('idle');
  const [emailAvailabilityMessage, setEmailAvailabilityMessage] = useState('');
  const [mobileAvailability, setMobileAvailability] = useState('idle');
  const [mobileAvailabilityMessage, setMobileAvailabilityMessage] = useState('');
  const [orgLookupStatus, setOrgLookupStatus] = useState('idle');
  const [orgLookupName, setOrgLookupName] = useState('');
  const [orgLookupMessage, setOrgLookupMessage] = useState('');
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [usernameAvailabilityMessage, setUsernameAvailabilityMessage] = useState('');

  const emailDebounceRef = useRef(null);
  const mobileDebounceRef = useRef(null);
  const orgDebounceRef = useRef(null);
  const usernameDebounceRef = useRef(null);
  const stepTransitionRef = useRef(false);
  const prevUsernameAvailability = useRef('idle');
  const usernameCheckSeq = useRef(0);

  const [hasAttemptedStep3Submit, setHasAttemptedStep3Submit] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loading,
    error,
    isAuthenticated,
    registrationSuccess,
    user,
    otpLoading,
    otpError,
    emailOtpLoading,
    emailOtpError,
  } = useSelector((state) => state.auth);

  const maxDob = getMaxDobForMinAge(MIN_AGE_YEARS);
  const stepLabels = accountType === 'individual'
    ? ['Profile', 'Contact', 'Security']
    : ['Organization', 'Owner', 'Security'];

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role === 'ADMIN') {
        navigate('/admin');
      } else {
        navigate('/chat');
      }
    }
    if (registrationSuccess) {
      setShowSuccess(true);
    }
  }, [isAuthenticated, navigate, registrationSuccess, user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined' || !parseJwtSubject(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  }, []);

  useEffect(() => {
    setErrors({});
    setHasAttemptedStep3Submit(false);
  }, [currentStep]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearOtpState());
      dispatch(clearEmailOtpState());
      dispatch(clearRegistrationSuccess());
    };
  }, [dispatch]);

  useEffect(() => {
    if (otpTimer <= 0) return undefined;
    const interval = setInterval(() => setOtpTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  useEffect(() => {
    if (emailOtpTimer <= 0) return undefined;
    const interval = setInterval(() => setEmailOtpTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [emailOtpTimer]);

  useEffect(() => {
    if (otpError) {
      setOtpVerified(false);
    }
  }, [otpError]);

  useEffect(() => {
    if (emailOtpError) {
      setEmailOtpVerified(false);
    }
  }, [emailOtpError]);

  const resetMobileVerification = useCallback(() => {
    setMobileAvailability('idle');
    setMobileAvailabilityMessage('');
    setOtpVerified(false);
    setOtpSent(false);
    setOtpTimer(0);
    dispatch(clearOtpState());
  }, [dispatch]);

  const resetEmailVerification = useCallback(() => {
    setEmailAvailability('idle');
    setEmailAvailabilityMessage('');
    setEmailOtpVerified(false);
    setEmailOtpSent(false);
    setEmailOtpTimer(0);
    dispatch(clearEmailOtpState());
    resetMobileVerification();
  }, [dispatch, resetMobileVerification]);

  const resetUsernameVerification = useCallback(() => {
    setUsernameAvailability('idle');
    setUsernameCheckedValue('');
    setUsernameAvailabilityMessage('');
    resetEmailVerification();
  }, [resetEmailVerification]);

  const getEmailOtpUsername = () => (accountType === 'individual' ? formData.username : organizationFormData.ownerUsername);
  const getOtpMobile = () => (accountType === 'individual' ? formData.mobile : organizationFormData.ownerMobile);
  const isUsernameConfirmed = () => {
    const username = (getEmailOtpUsername() || '').trim();
    return usernameAvailability === 'available' && usernameCheckedValue === username && username.length > 0;
  };

  const runUsernameAvailabilityCheck = useCallback(async (username) => {
    const trimmed = (username || '').trim();
    const seq = ++usernameCheckSeq.current;
    if (!trimmed) {
      setUsernameAvailability('idle');
      setUsernameCheckedValue('');
      setUsernameAvailabilityMessage('');
      return;
    }
    if (trimmed.length < 3 || trimmed.length > 20) {
      setUsernameAvailability('invalid');
      setUsernameCheckedValue('');
      setUsernameAvailabilityMessage('Username must be 3–20 characters');
      return;
    }
    if (!USERNAME_REGEX.test(trimmed)) {
      setUsernameAvailability('invalid');
      setUsernameCheckedValue('');
      setUsernameAvailabilityMessage('Use letters, numbers, dots, underscores, or hyphens only');
      return;
    }
    setUsernameCheckLoading(true);
    setUsernameAvailability('checking');
    setUsernameAvailabilityMessage('Checking username…');
    try {
      const result = await registrationService.checkUsername(trimmed);
      if (seq !== usernameCheckSeq.current) return;
      if (result.available) {
        setUsernameAvailability('available');
        setUsernameCheckedValue(trimmed);
        setUsernameAvailabilityMessage(result.message || 'Username is available');
      } else {
        setUsernameAvailability('taken');
        setUsernameCheckedValue('');
        setUsernameAvailabilityMessage(result.message || 'Username is already taken');
      }
    } catch (err) {
      if (seq !== usernameCheckSeq.current) return;
      setUsernameAvailability('error');
      setUsernameCheckedValue('');
      setUsernameAvailabilityMessage('Unable to verify username. Please try again.');
    } finally {
      if (seq === usernameCheckSeq.current) setUsernameCheckLoading(false);
    }
  }, []);

  const getCurrentEmail = () => (accountType === 'individual' ? formData.email : organizationFormData.ownerEmail).trim();

  const runEmailAvailabilityCheck = useCallback(async (email) => {
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailAvailability('invalid');
      setEmailAvailabilityMessage('Enter a valid email address');
      return;
    }
    setEmailAvailability('checking');
    try {
      const result = await registrationService.checkEmail(email);
      if (result.available) {
        setEmailAvailability('available');
        setEmailAvailabilityMessage(result.message || 'Email is available');
      } else {
        setEmailAvailability('taken');
        setEmailAvailabilityMessage(result.message || 'Email is already registered');
        setEmailOtpSent(false);
        setEmailOtpVerified(false);
      }
    } catch (err) {
      setEmailAvailability('error');
      setEmailAvailabilityMessage('Unable to verify email. Please try again.');
    }
  }, []);

  const runMobileAvailabilityCheck = useCallback(async (mobile) => {
    const normalized = (mobile || '').replace(/\D/g, '');
    if (normalized.length !== 10) {
      setMobileAvailability('invalid');
      setMobileAvailabilityMessage('Enter a valid 10-digit mobile number');
      return;
    }
    setMobileAvailability('checking');
    try {
      const result = await registrationService.checkMobile(normalized);
      if (result.available) {
        setMobileAvailability('available');
        setMobileAvailabilityMessage(result.message || 'Mobile number is available');
      } else {
        setMobileAvailability('taken');
        setMobileAvailabilityMessage(result.message || 'Mobile number is already registered');
        setOtpSent(false);
        setOtpVerified(false);
      }
    } catch (err) {
      setMobileAvailability('error');
      setMobileAvailabilityMessage('Unable to verify mobile number. Please try again.');
    }
  }, []);

  const runOrgLookup = useCallback(async (slug) => {
    const trimmed = (slug || '').trim().toLowerCase();
    if (!trimmed) {
      setOrgLookupStatus('idle');
      setOrgLookupName('');
      setOrgLookupMessage('');
      return;
    }
    if (!SLUG_REGEX.test(trimmed)) {
      setOrgLookupStatus('invalid');
      setOrgLookupName('');
      setOrgLookupMessage('Organization ID must use lowercase letters, numbers, and hyphens only');
      return;
    }
    setOrgLookupStatus('checking');
    try {
      const result = await registrationService.checkOrganization(trimmed);
      if (result.exists) {
        setOrgLookupStatus('found');
        setOrgLookupName(result.organizationName || '');
        setOrgLookupMessage(result.message || 'Organization found');
      } else {
        setOrgLookupStatus('not_found');
        setOrgLookupName('');
        setOrgLookupMessage(result.message || 'Organization not found');
      }
    } catch (err) {
      setOrgLookupStatus('invalid');
      setOrgLookupName('');
      setOrgLookupMessage('Unable to verify organization. Please try again.');
    }
  }, []);

  useEffect(() => {
    const username = (accountType === 'individual' ? formData.username : organizationFormData.ownerUsername) || '';
    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    if (!username.trim()) {
      setUsernameAvailability('idle');
      setUsernameCheckedValue('');
      setUsernameAvailabilityMessage('');
      setUsernameCheckLoading(false);
      return undefined;
    }
    usernameDebounceRef.current = setTimeout(() => {
      runUsernameAvailabilityCheck(username);
    }, DEBOUNCE_MS);
    return () => {
      if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    };
  }, [formData.username, organizationFormData.ownerUsername, accountType, runUsernameAvailabilityCheck]);

  useEffect(() => {
    if (
      usernameAvailability === 'available'
      && prevUsernameAvailability.current !== 'available'
      && !emailOtpVerified
    ) {
      setVerifyModalOpen(true);
    }
    prevUsernameAvailability.current = usernameAvailability;
  }, [usernameAvailability, emailOtpVerified]);

  useEffect(() => {
    if (!isUsernameConfirmed()) return undefined;
    const email = getCurrentEmail();
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    if (!email) {
      setEmailAvailability('idle');
      setEmailAvailabilityMessage('');
      return undefined;
    }
    emailDebounceRef.current = setTimeout(() => {
      runEmailAvailabilityCheck(email);
    }, DEBOUNCE_MS);
    return () => {
      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    };
  }, [formData.email, organizationFormData.ownerEmail, accountType, usernameCheckedValue, usernameAvailability, runEmailAvailabilityCheck]);

  useEffect(() => {
    if (!emailOtpVerified) return undefined;
    const mobile = getOtpMobile();
    if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current);
    if (!mobile) {
      setMobileAvailability('idle');
      setMobileAvailabilityMessage('');
      return undefined;
    }
    mobileDebounceRef.current = setTimeout(() => {
      runMobileAvailabilityCheck(mobile);
    }, DEBOUNCE_MS);
    return () => {
      if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current);
    };
  }, [formData.mobile, organizationFormData.ownerMobile, accountType, emailOtpVerified, runMobileAvailabilityCheck]);

  useEffect(() => {
    if (!hasCustomSlug || accountType !== 'individual') return undefined;
    const slug = formData.organizationSlug;
    if (orgDebounceRef.current) clearTimeout(orgDebounceRef.current);
    if (!slug?.trim()) {
      setOrgLookupStatus('idle');
      setOrgLookupName('');
      setOrgLookupMessage('');
      return undefined;
    }
    orgDebounceRef.current = setTimeout(() => {
      runOrgLookup(slug);
    }, DEBOUNCE_MS);
    return () => {
      if (orgDebounceRef.current) clearTimeout(orgDebounceRef.current);
    };
  }, [formData.organizationSlug, hasCustomSlug, accountType, runOrgLookup]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'username') {
      resetUsernameVerification();
    }
    if (name === 'email' || name === 'emailOtp') {
      setEmailOtpVerified(false);
      if (name === 'email') {
        setEmailOtpSent(false);
        resetEmailVerification();
        setEmailAvailability('idle');
        setEmailAvailabilityMessage('');
      }
      dispatch(clearEmailOtpState());
    }
    if (name === 'mobile' || name === 'otp') {
      setOtpVerified(false);
      if (name === 'mobile') {
        setOtpSent(false);
        setMobileAvailability('idle');
        setMobileAvailabilityMessage('');
      }
      dispatch(clearOtpState());
    }
    if (name === 'organizationSlug') {
      setOrgLookupStatus('idle');
      setOrgLookupName('');
      setOrgLookupMessage('');
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleOrganizationChange = (e) => {
    const { name, value } = e.target;
    setOrganizationFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'ownerUsername') {
      resetUsernameVerification();
    }
    if (name === 'ownerEmail' || name === 'ownerEmailOtp') {
      setEmailOtpVerified(false);
      if (name === 'ownerEmail') {
        setEmailOtpSent(false);
        resetEmailVerification();
        setEmailAvailability('idle');
        setEmailAvailabilityMessage('');
      }
      dispatch(clearEmailOtpState());
    }
    if (name === 'ownerMobile' || name === 'ownerOtp') {
      setOtpVerified(false);
      if (name === 'ownerMobile') {
        setOtpSent(false);
        setMobileAvailability('idle');
        setMobileAvailabilityMessage('');
      }
      dispatch(clearOtpState());
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSwitchAccountType = (type) => {
    setAccountType(type);
    setCurrentStep(1);
    setHasCustomSlug(false);
    setErrors({});
    setShowSuccess(false);
    setOtpVerified(false);
    setOtpSent(false);
    setOtpTimer(0);
    setEmailOtpVerified(false);
    setEmailOtpSent(false);
    setEmailOtpTimer(0);
    resetUsernameVerification();
    setOrgLookupStatus('idle');
    setOrgLookupName('');
    setOrgLookupMessage('');
    dispatch(clearOtpState());
    dispatch(clearEmailOtpState());
  };

  const handleCustomSlugToggle = (checked) => {
    setHasCustomSlug(checked);
    setFormData((prev) => ({ ...prev, organizationSlug: '' }));
    setOrgLookupStatus('idle');
    setOrgLookupName('');
    setOrgLookupMessage('');
    setErrors((prev) => {
      const next = { ...prev };
      delete next.organizationSlug;
      return next;
    });
  };

  const getOtpValue = () => (accountType === 'individual' ? formData.otp : organizationFormData.ownerOtp);
  const getOtpErrorKey = () => (accountType === 'individual' ? 'otp' : 'ownerOtp');
  const getMobileErrorKey = () => (accountType === 'individual' ? 'mobile' : 'ownerMobile');
  const getEmailOtpEmail = () => (accountType === 'individual' ? formData.email : organizationFormData.ownerEmail);
  const getEmailOtpValue = () => (accountType === 'individual' ? formData.emailOtp : organizationFormData.ownerEmailOtp);
  const getEmailOtpErrorKey = () => (accountType === 'individual' ? 'emailOtp' : 'ownerEmailOtp');

  const generateSlugFromName = () => {
    const slug = organizationFormData.organizationName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    setOrganizationFormData((prev) => ({ ...prev, organizationSlug: slug }));
    if (errors.organizationSlug) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.organizationSlug;
        return next;
      });
    }
  };

  const validateOtpFields = (otpKey, mobileKey, mobileValue, otpValue) => {
    const newErrors = {};
    if (!/^\d{10}$/.test(mobileValue || '')) newErrors[mobileKey] = 'Enter valid 10-digit mobile number';
    const normalizedOtp = (otpValue || '').replace(/\D/g, '');
    if (!otpSent) newErrors[otpKey] = 'Please click Send OTP first';
    else if (!normalizedOtp) newErrors[otpKey] = 'Please enter OTP';
    else if (!otpVerified) newErrors[otpKey] = 'Please verify OTP before continuing';
    return newErrors;
  };

  const validateEmailOtpFields = (otpKey, usernameKey, emailKey, usernameValue, emailValue, otpValue) => {
    const newErrors = {};
    if (!usernameValue?.trim()) newErrors[usernameKey] = 'Username is required';
    if (!emailValue?.trim()) newErrors[emailKey] = 'Email is required';
    else if (!EMAIL_REGEX.test(emailValue)) newErrors[emailKey] = 'Email is invalid';
    const normalizedOtp = (otpValue || '').replace(/\D/g, '');
    if (!emailOtpSent) newErrors[otpKey] = 'Please click Send Email OTP first';
    else if (!normalizedOtp) newErrors[otpKey] = 'Please enter email OTP';
    else if (!emailOtpVerified) newErrors[otpKey] = 'Please verify email OTP before continuing';
    return newErrors;
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (accountType === 'individual') {
      if (step === 1) {
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        else if (formData.dateOfBirth > maxDob) {
          newErrors.dateOfBirth = `You must be at least ${MIN_AGE_YEARS} years old.`;
        }
      } else if (step === 2) {
        if (!formData.username.trim()) newErrors.username = 'Username is required';
        else if (!isUsernameConfirmed()) newErrors.username = usernameAvailabilityMessage || 'Please choose an available username';
        if (isUsernameConfirmed()) {
          Object.assign(
            newErrors,
            validateEmailOtpFields('emailOtp', 'username', 'email', formData.username, formData.email, formData.emailOtp)
          );
          if (emailAvailability !== 'available') {
            newErrors.email = emailAvailabilityMessage || 'Please enter an available email address';
          }
        }
        if (emailOtpVerified) {
          Object.assign(newErrors, validateOtpFields('otp', 'mobile', formData.mobile, formData.otp));
          if (mobileAvailability !== 'available') {
            newErrors.mobile = mobileAvailabilityMessage || 'Please enter an available mobile number';
          }
        }
        if (hasCustomSlug) {
          if (!formData.organizationSlug.trim()) newErrors.organizationSlug = 'Organization ID is required';
          else if (!SLUG_REGEX.test(formData.organizationSlug.trim())) {
            newErrors.organizationSlug = 'Organization ID must use lowercase letters, numbers, and hyphens only';
          } else if (orgLookupStatus !== 'found') {
            newErrors.organizationSlug = orgLookupMessage || 'Organization not found';
          }
        }
      } else if (step === 3) {
        if (!formData.password) newErrors.password = 'Password is required';
        else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
        if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
        else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      }
    } else {
      if (step === 1) {
        if (!organizationFormData.organizationName.trim()) newErrors.organizationName = 'Organization name is required';
        if (!organizationFormData.organizationSlug.trim()) newErrors.organizationSlug = 'Organization slug is required';
        else if (!SLUG_REGEX.test(organizationFormData.organizationSlug.trim())) {
          newErrors.organizationSlug = 'Slug must be lowercase letters, numbers, and hyphens only';
        }
      } else if (step === 2) {
        if (!organizationFormData.ownerUsername.trim()) newErrors.ownerUsername = 'Owner username is required';
        else if (!isUsernameConfirmed()) newErrors.ownerUsername = usernameAvailabilityMessage || 'Please choose an available username';
        if (isUsernameConfirmed()) {
          Object.assign(
            newErrors,
            validateEmailOtpFields(
              'ownerEmailOtp',
              'ownerUsername',
              'ownerEmail',
              organizationFormData.ownerUsername,
              organizationFormData.ownerEmail,
              organizationFormData.ownerEmailOtp
            )
          );
          if (emailAvailability !== 'available') {
            newErrors.ownerEmail = emailAvailabilityMessage || 'Please enter an available email address';
          }
        }
        if (emailOtpVerified) {
          Object.assign(
            newErrors,
            validateOtpFields(
              'ownerOtp',
              'ownerMobile',
              organizationFormData.ownerMobile,
              organizationFormData.ownerOtp
            )
          );
          if (mobileAvailability !== 'available') {
            newErrors.ownerMobile = mobileAvailabilityMessage || 'Please enter an available mobile number';
          }
        }
      } else if (step === 3) {
        if (!organizationFormData.ownerPassword) newErrors.ownerPassword = 'Owner password is required';
        else if (organizationFormData.ownerPassword.length < 8) newErrors.ownerPassword = 'Password must be at least 8 characters';
        if (!organizationFormData.confirmOwnerPassword) {
          newErrors.confirmOwnerPassword = 'Please confirm your password';
        } else if (organizationFormData.ownerPassword !== organizationFormData.confirmOwnerPassword) {
          newErrors.confirmOwnerPassword = 'Passwords do not match';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 3) {
        stepTransitionRef.current = true;
        window.setTimeout(() => {
          stepTransitionRef.current = false;
        }, 100);
      }
      setErrors({});
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    setErrors({});
    setHasAttemptedStep3Submit(false);
    setCurrentStep((prev) => prev - 1);
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter' || currentStep >= 3) return;
    if (e.target.tagName === 'TEXTAREA') return;
    e.preventDefault();
    nextStep();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (stepTransitionRef.current) return;
    if (currentStep < 3) return;
    setHasAttemptedStep3Submit(true);
    if (validateStep(3)) {
      if (accountType === 'individual') {
        const { confirmPassword, otp, gender, organizationSlug, ...rest } = formData;
        const slug = hasCustomSlug ? organizationSlug.trim() : 'global';
        dispatch(register({ ...rest, organizationSlug: slug }));
      } else {
        const { organizationName, organizationSlug, ownerEmail, ownerUsername, ownerPassword, ownerMobile } = organizationFormData;
        dispatch(
          organizationSignup({
            organizationName: organizationName.trim(),
            organizationSlug: organizationSlug.trim(),
            ownerEmail: ownerEmail.trim(),
            ownerUsername: ownerUsername.trim(),
            ownerPassword,
            ownerMobile: ownerMobile.trim(),
          })
        );
      }
    }
  };

  const handleSendOtp = async () => {
    const rawMobile = getOtpMobile() || '';
    const normalized = rawMobile.replace(/\D/g, '');
    const mobileErrorKey = getMobileErrorKey();
    if (normalized.length !== 10) {
      setErrors((prev) => ({
        ...prev,
        [mobileErrorKey]: 'Enter valid 10-digit mobile number',
      }));
      return;
    }
    if (mobileAvailability !== 'available') {
      setErrors((prev) => ({
        ...prev,
        [mobileErrorKey]: mobileAvailabilityMessage || 'Please enter an available mobile number',
      }));
      return;
    }
    dispatch(clearOtpState());
    const result = await dispatch(sendOtp(normalized));
    const isOtpSent = sendOtp.fulfilled.match(result) && result.payload?.success !== false;
    if (isOtpSent) {
      setOtpVerified(false);
      setOtpSent(true);
      setOtpTimer(60);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.otp;
        delete next.ownerOtp;
        return next;
      });
    } else {
      setOtpSent(false);
      setOtpVerified(false);
      const backendMessage =
        typeof result?.payload === 'string'
          ? result.payload
          : result?.payload?.message || 'Failed to send OTP';
      setErrors((prev) => ({
        ...prev,
        [mobileErrorKey]: backendMessage,
      }));
    }
  };

  const handleVerifyOtp = async (otpOverride) => {
    const normalized = (getOtpMobile() || '').replace(/\D/g, '');
    const otp = String(otpOverride ?? getOtpValue() ?? '').replace(/\D/g, '');
    const otpKey = getOtpErrorKey();
    if (!otpSent) {
      setErrors((prev) => ({
        ...prev,
        [otpKey]: 'Please click Send OTP first',
      }));
      return;
    }
    if (normalized.length !== 10 || otp.length !== 6) {
      setErrors((prev) => ({
        ...prev,
        [otpKey]: 'Enter valid mobile and 6-digit OTP',
      }));
      return;
    }
    const result = await dispatch(verifyOtp({ mobile: normalized, otp }));
    if (verifyOtp.fulfilled.match(result)) {
      setOtpVerified(true);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.otp;
        delete next.ownerOtp;
        return next;
      });
    }
  };

  const handleResendOtp = async () => {
    if (otpTimer > 0) return;
    const normalized = (getOtpMobile() || '').replace(/\D/g, '');
    if (normalized.length !== 10) return;
    const result = await dispatch(resendOtp(normalized));
    if (resendOtp.fulfilled.match(result)) {
      setOtpVerified(false);
      setOtpSent(true);
      setOtpTimer(60);
    }
  };

  const handleSendEmailOtp = async () => {
    const username = (getEmailOtpUsername() || '').trim();
    const email = (getEmailOtpEmail() || '').trim();
    const otpKey = getEmailOtpErrorKey();
    if (!username) {
      setErrors((prev) => ({ ...prev, [accountType === 'individual' ? 'username' : 'ownerUsername']: 'Username is required' }));
      return;
    }
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrors((prev) => ({ ...prev, [accountType === 'individual' ? 'email' : 'ownerEmail']: 'Enter a valid email address' }));
      return;
    }
    if (emailAvailability !== 'available') {
      setErrors((prev) => ({
        ...prev,
        [accountType === 'individual' ? 'email' : 'ownerEmail']:
          emailAvailabilityMessage || 'Please enter an available email address',
      }));
      return;
    }
    dispatch(clearEmailOtpState());
    const result = await dispatch(sendEmailOtp({ username, email }));
    const isSent = sendEmailOtp.fulfilled.match(result) && result.payload?.success !== false;
    if (isSent) {
      setEmailOtpVerified(false);
      setEmailOtpSent(true);
      setEmailOtpTimer(60);
      setErrors((prev) => {
        const next = { ...prev };
        delete next[otpKey];
        return next;
      });
    } else {
      setEmailOtpSent(false);
      setEmailOtpVerified(false);
    }
  };

  const handleVerifyEmailOtp = async (otpOverride) => {
    const username = (getEmailOtpUsername() || '').trim();
    const otp = String(otpOverride ?? getEmailOtpValue() ?? '').replace(/\D/g, '');
    const otpKey = getEmailOtpErrorKey();
    if (!emailOtpSent) {
      setErrors((prev) => ({ ...prev, [otpKey]: 'Please click Send Email OTP first' }));
      return;
    }
    if (!username || otp.length !== 6) {
      setErrors((prev) => ({ ...prev, [otpKey]: 'Enter valid username and 6-digit OTP' }));
      return;
    }
    const result = await dispatch(verifyEmailOtp({ username, email: getEmailOtpEmail().trim(), otp }));
    if (verifyEmailOtp.fulfilled.match(result)) {
      setEmailOtpVerified(true);
      setErrors((prev) => {
        const next = { ...prev };
        delete next[otpKey];
        return next;
      });
    }
  };

  const handleResendEmailOtp = async () => {
    if (emailOtpTimer > 0) return;
    const username = (getEmailOtpUsername() || '').trim();
    const email = (getEmailOtpEmail() || '').trim();
    if (!username || !email) return;
    const result = await dispatch(resendEmailOtp({ username, email }));
    if (resendEmailOtp.fulfilled.match(result)) {
      setEmailOtpVerified(false);
      setEmailOtpSent(true);
      setEmailOtpTimer(60);
    }
  };

  const handleCheckUsername = () => {
    runUsernameAvailabilityCheck(getEmailOtpUsername());
  };

  const usernameStatusClass = (status) => {
    if (status === 'available') return 'text-emerald-600 dark:text-emerald-400';
    if (status === 'checking') return 'text-gray-500 dark:text-gray-400';
    if (status === 'idle') return 'text-gray-500 dark:text-gray-400';
    return 'text-red-500';
  };

  const renderAvailabilityMessage = (status, message) => {
    if (!message || status === 'idle') return null;
    const colorClass =
      status === 'available' || status === 'found'
        ? 'text-green-600 dark:text-green-400'
        : status === 'checking'
          ? 'text-gray-500 dark:text-gray-400'
          : 'text-red-500';
    return <p className={`mt-1 text-sm ${colorClass}`}>{message}</p>;
  };

  const renderUsernameRow = (usernameName, usernameValue, usernameErrorKey) => (
    <div>
      <label htmlFor={usernameName} className={LABEL_CLASS}>Username</label>
      <div className="relative">
        <input
          id={usernameName}
          name={usernameName}
          type="text"
          autoComplete="username"
          maxLength={20}
          className={`w-full pr-24 ${inputClass(
            !!errors[usernameErrorKey]
            || usernameAvailability === 'taken'
            || usernameAvailability === 'invalid'
            || usernameAvailability === 'error'
          )}`}
          placeholder={usernameName === 'ownerUsername' ? 'admin_acme' : 'johndoe123'}
          value={usernameValue}
          onChange={(e) => {
            const next = e.target.value.replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 20);
            const handler = accountType === 'individual' ? handleChange : handleOrganizationChange;
            handler({ target: { name: usernameName, value: next } });
          }}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {usernameCheckLoading || usernameAvailability === 'checking' ? (
            <span className="text-xs text-gray-400">Checking…</span>
          ) : usernameAvailability === 'available' && usernameCheckedValue === usernameValue.trim() ? (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white" title="Available">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : usernameAvailability === 'taken' || usernameAvailability === 'invalid' || usernameAvailability === 'error' ? (
            <button
              type="button"
              onClick={handleCheckUsername}
              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              Retry
            </button>
          ) : null}
        </div>
      </div>
      {errors[usernameErrorKey] && <p className="mt-1 text-sm text-red-500">{errors[usernameErrorKey]}</p>}
      {!errors[usernameErrorKey] && usernameAvailabilityMessage && (
        <p className={`mt-1 text-sm ${usernameStatusClass(usernameAvailability)}`}>{usernameAvailabilityMessage}</p>
      )}
    </div>
  );

  const renderVerifyAccountCard = () => (
    <div className="rounded-xl border border-gray-200 dark:border-gray-600 p-4 bg-gray-50/60 dark:bg-gray-900/30 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Verify Account</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Confirm your email, then your mobile number
          </p>
        </div>
        <button
          type="button"
          onClick={() => setVerifyModalOpen(true)}
          className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 whitespace-nowrap"
        >
          {otpVerified ? 'View' : emailOtpVerified ? 'Continue' : 'Verify'}
        </button>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          {emailOtpVerified ? (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-[10px] text-gray-400">1</span>
          )}
          <span className={emailOtpVerified ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-600 dark:text-gray-400'}>
            {emailOtpVerified ? `Email verified · ${getCurrentEmail()}` : 'Email not verified'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {otpVerified ? (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-[10px] text-gray-400">2</span>
          )}
          <span className={otpVerified ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-600 dark:text-gray-400'}>
            {otpVerified ? `Mobile verified · ${getOtpMobile()}` : 'Mobile not verified'}
          </span>
        </div>
      </div>
      {(errors.email || errors.emailOtp || errors.ownerEmail || errors.ownerEmailOtp || errors.mobile || errors.otp || errors.ownerMobile || errors.ownerOtp) && !verifyModalOpen ? (
        <p className="text-sm text-red-500">
          {errors.emailOtp || errors.ownerEmailOtp || errors.otp || errors.ownerOtp || errors.email || errors.ownerEmail || errors.mobile || errors.ownerMobile}
        </p>
      ) : null}
    </div>
  );

  const showStep3Errors = hasAttemptedStep3Submit;

  const StepIndicator = () => (
    <div className="flex items-center justify-between mb-8 w-full">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center relative">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                currentStep >= step
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500'
              }`}
            >
              {currentStep > step ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-sm font-semibold">{step}</span>
              )}
            </div>
            <span
              className={`absolute -bottom-6 text-xs font-medium whitespace-nowrap ${
                currentStep === step ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {stepLabels[step - 1]}
            </span>
          </div>
          {step < 3 && (
            <div
              className={`flex-1 h-0.5 mx-2 transition-all duration-500 ${
              currentStep > step ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden">
      <AuthBrandingPanel />

      <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="w-full max-w-md py-4">
          <AuthMobileHeader />

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
          {!showSuccess ? (
            <>
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create your account</h2>
                  <p className="text-gray-600 dark:text-gray-400">Join the conversation and start collaborating</p>
                </div>

              {currentStep === 1 && (
                  <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-lg mb-6">
                  <button
                    type="button"
                    onClick={() => handleSwitchAccountType('individual')}
                      className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                      accountType === 'individual'
                        ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Individual
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSwitchAccountType('organization')}
                      className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${
                      accountType === 'organization'
                        ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Organization
                  </button>
                </div>
              )}

              <StepIndicator />

                <form className="space-y-5 mt-10" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
                {accountType === 'individual' ? (
                  <>
                    {currentStep === 1 && (
                        <div className="space-y-5">
                        <div>
                            <label htmlFor="firstName" className={LABEL_CLASS}>First Name</label>
                          <input
                              id="firstName"
                            name="firstName"
                            type="text"
                              className={inputClass(!!errors.firstName)}
                            placeholder="e.g. John"
                            value={formData.firstName}
                            onChange={handleChange}
                          />
                            {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
                        </div>
                        <div>
                            <label htmlFor="lastName" className={LABEL_CLASS}>Last Name</label>
                          <input
                              id="lastName"
                            name="lastName"
                            type="text"
                              className={inputClass(!!errors.lastName)}
                            placeholder="e.g. Doe"
                            value={formData.lastName}
                            onChange={handleChange}
                          />
                            {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
                        </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                              <label htmlFor="gender" className={LABEL_CLASS}>Gender</label>
                              <select
                                id="gender"
                                name="gender"
                                className={inputClass(!!errors.gender)}
                                value={formData.gender}
                            onChange={handleChange}
                              >
                                <option value="">Select gender</option>
                                {GENDER_OPTIONS.map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                              {errors.gender && <p className="mt-1 text-sm text-red-500">{errors.gender}</p>}
                        </div>
                        <div>
                              <label htmlFor="dateOfBirth" className={LABEL_CLASS}>Date of Birth</label>
                          <input
                                id="dateOfBirth"
                                name="dateOfBirth"
                                type="date"
                                max={maxDob}
                                className={inputClass(!!errors.dateOfBirth)}
                                value={formData.dateOfBirth}
                            onChange={handleChange}
                          />
                              {errors.dateOfBirth && <p className="mt-1 text-sm text-red-500">{errors.dateOfBirth}</p>}
                        </div>
                          </div>
                        </div>
                      )}

                      {currentStep === 2 && (
                        <div className="space-y-5">
                          {renderUsernameRow('username', formData.username, 'username')}

                          {isUsernameConfirmed() && renderVerifyAccountCard()}

                          <div className="pt-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={hasCustomSlug}
                                onChange={(e) => handleCustomSlugToggle(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Are you joining an organization?</span>
                          </label>
                            {hasCustomSlug && (
                              <div className="mt-3">
                                <label htmlFor="organizationSlug" className={LABEL_CLASS}>Organization ID</label>
                          <input
                                  id="organizationSlug"
                            name="organizationSlug"
                            type="text"
                                  className={inputClass(!!errors.organizationSlug || orgLookupStatus === 'not_found' || orgLookupStatus === 'invalid')}
                            placeholder="my-organization"
                            value={formData.organizationSlug}
                            onChange={handleChange}
                          />
                                {errors.organizationSlug && (
                                  <p className="mt-1 text-sm text-red-500">{errors.organizationSlug}</p>
                                )}
                                {!errors.organizationSlug && orgLookupStatus === 'found' && orgLookupName && (
                                  <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                                    {orgLookupName}
                                  </p>
                                )}
                                {!errors.organizationSlug && orgLookupStatus !== 'found' && orgLookupMessage && (
                                  renderAvailabilityMessage(orgLookupStatus === 'checking' ? 'checking' : 'invalid', orgLookupMessage)
                                )}
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                  Enter the organization ID shared by your team
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    {currentStep === 3 && (
                        <div className="space-y-5">
                        <div>
                            <label htmlFor="password" className={LABEL_CLASS}>Password</label>
                          <div className="relative">
                            <input
                                id="password"
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                                className={`${inputClass(showStep3Errors && !!errors.password)} pr-11`}
                              placeholder="Min 8 characters"
                              value={formData.password}
                              onChange={handleChange}
                            />
                              <PasswordToggle show={showPassword} onToggle={() => setShowPassword((p) => !p)} />
                          </div>
                            {showStep3Errors && errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className={LABEL_CLASS}>Confirm Password</label>
                          <div className="relative">
                            <input
                                id="confirmPassword"
                              name="confirmPassword"
                              type={showConfirmPassword ? 'text' : 'password'}
                                className={`${inputClass(showStep3Errors && !!errors.confirmPassword)} pr-11`}
                              placeholder="Repeat password"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                            />
                              <PasswordToggle show={showConfirmPassword} onToggle={() => setShowConfirmPassword((p) => !p)} />
                            </div>
                            {showStep3Errors && errors.confirmPassword && (
                              <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                            )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {currentStep === 1 && (
                        <div className="space-y-5">
                        <div>
                            <label htmlFor="organizationName" className={LABEL_CLASS}>Organization Name</label>
                          <input
                              id="organizationName"
                            name="organizationName"
                            type="text"
                              className={inputClass(!!errors.organizationName)}
                            placeholder="e.g. Acme Corp"
                            value={organizationFormData.organizationName}
                            onChange={handleOrganizationChange}
                          />
                            {errors.organizationName && <p className="mt-1 text-sm text-red-500">{errors.organizationName}</p>}
                        </div>
                        <div>
                            <label htmlFor="orgSlug" className={LABEL_CLASS}>Organization Slug</label>
                          <div className="flex gap-2">
                            <input
                                id="orgSlug"
                              name="organizationSlug"
                              type="text"
                                className={`flex-1 ${inputClass(!!errors.organizationSlug)}`}
                              placeholder="acme-corp"
                              value={organizationFormData.organizationSlug}
                              onChange={handleOrganizationChange}
                            />
                            <button
                              type="button"
                              onClick={generateSlugFromName}
                                className="px-4 py-3 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                            >
                              Auto
                            </button>
                          </div>
                            {errors.organizationSlug && <p className="mt-1 text-sm text-red-500">{errors.organizationSlug}</p>}
                        </div>
                      </div>
                    )}

                    {currentStep === 2 && (
                        <div className="space-y-5">
                          {renderUsernameRow('ownerUsername', organizationFormData.ownerUsername, 'ownerUsername')}

                          {isUsernameConfirmed() && renderVerifyAccountCard()}
                      </div>
                    )}

                    {currentStep === 3 && (
                        <div className="space-y-5">
                        <div>
                            <label htmlFor="ownerPassword" className={LABEL_CLASS}>Admin Password</label>
                          <div className="relative">
                            <input
                                id="ownerPassword"
                              name="ownerPassword"
                              type={showPassword ? 'text' : 'password'}
                                className={`${inputClass(showStep3Errors && !!errors.ownerPassword)} pr-11`}
                              placeholder="Min 8 characters"
                              value={organizationFormData.ownerPassword}
                              onChange={handleOrganizationChange}
                            />
                              <PasswordToggle show={showPassword} onToggle={() => setShowPassword((p) => !p)} />
                          </div>
                            {showStep3Errors && errors.ownerPassword && (
                              <p className="mt-1 text-sm text-red-500">{errors.ownerPassword}</p>
                            )}
                            </div>
                        <div>
                            <label htmlFor="confirmOwnerPassword" className={LABEL_CLASS}>Confirm Admin Password</label>
                          <div className="relative">
                            <input
                                id="confirmOwnerPassword"
                              name="confirmOwnerPassword"
                              type={showConfirmPassword ? 'text' : 'password'}
                                className={`${inputClass(showStep3Errors && !!errors.confirmOwnerPassword)} pr-11`}
                              placeholder="Repeat password"
                              value={organizationFormData.confirmOwnerPassword}
                              onChange={handleOrganizationChange}
                            />
                              <PasswordToggle show={showConfirmPassword} onToggle={() => setShowConfirmPassword((p) => !p)} />
                            </div>
                            {showStep3Errors && errors.confirmOwnerPassword && (
                              <p className="mt-1 text-sm text-red-500">{errors.confirmOwnerPassword}</p>
                            )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {error && (
                    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
                      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                )}

                  <div className="flex gap-3 pt-2">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                        className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                    >
                      Back
                    </button>
                  )}
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                        className="flex-[2] py-3 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
                    >
                      Next Step
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                        className="flex-[2] py-3 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {loading ? 'Creating Account...' : 'Complete Registration'}
                    </button>
                  )}
                </div>
              </form>
            </>
          ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Registration Successful!</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                {accountType === 'individual' && !hasCustomSlug
                  ? 'Your account is ready. You can sign in now — no admin approval is required.'
                  : accountType === 'individual'
                    ? "Your account has been created and is pending admin approval. You'll be notified once verified."
                    : 'Your organization and admin account have been created. Access will be granted after verification.'}
              </p>
              <Link
                to="/login"
                  onClick={() => dispatch(clearRegistrationSuccess())}
                  className="inline-block w-full py-3 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-all"
              >
                Back to Login
              </Link>
            </div>
          )}
        </div>

          <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors">
            Sign in
          </Link>
        </p>
          <div className="mt-4 flex items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/privacy" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Privacy Policy</Link>
            <span aria-hidden>·</span>
            <Link to="/terms" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Terms</Link>
            <span aria-hidden>·</span>
            <Link to="/support" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Support</Link>
        </div>
      </div>
      </div>
      <VerifyAccountModal
        open={verifyModalOpen && isUsernameConfirmed()}
        onClose={() => setVerifyModalOpen(false)}
        username={getEmailOtpUsername()}
        email={getCurrentEmail()}
        onEmailChange={(value) => {
          const name = accountType === 'individual' ? 'email' : 'ownerEmail';
          const handler = accountType === 'individual' ? handleChange : handleOrganizationChange;
          handler({ target: { name, value } });
        }}
        emailAvailability={emailAvailability}
        emailAvailabilityMessage={emailAvailabilityMessage}
        emailOtp={getEmailOtpValue()}
        onEmailOtpChange={(value) => {
          const name = accountType === 'individual' ? 'emailOtp' : 'ownerEmailOtp';
          const handler = accountType === 'individual' ? handleChange : handleOrganizationChange;
          handler({ target: { name, value: String(value).replace(/\D/g, '').slice(0, 6) } });
        }}
        emailOtpSent={emailOtpSent}
        emailOtpVerified={emailOtpVerified}
        emailOtpLoading={emailOtpLoading}
        emailOtpError={emailOtpError}
        emailOtpTimer={emailOtpTimer}
        emailFieldError={errors[accountType === 'individual' ? 'email' : 'ownerEmail'] || errors[getEmailOtpErrorKey()]}
        onSendEmailOtp={handleSendEmailOtp}
        onVerifyEmailOtp={handleVerifyEmailOtp}
        onResendEmailOtp={handleResendEmailOtp}
        mobile={getOtpMobile()}
        onMobileChange={(value) => {
          const name = accountType === 'individual' ? 'mobile' : 'ownerMobile';
          const handler = accountType === 'individual' ? handleChange : handleOrganizationChange;
          handler({ target: { name, value: String(value).replace(/\D/g, '').slice(0, 10) } });
        }}
        mobileAvailability={mobileAvailability}
        mobileAvailabilityMessage={mobileAvailabilityMessage}
        mobileOtp={getOtpValue()}
        onMobileOtpChange={(value) => {
          const name = accountType === 'individual' ? 'otp' : 'ownerOtp';
          const handler = accountType === 'individual' ? handleChange : handleOrganizationChange;
          handler({ target: { name, value: String(value).replace(/\D/g, '').slice(0, 6) } });
        }}
        otpSent={otpSent}
        otpVerified={otpVerified}
        otpLoading={otpLoading}
        otpError={otpError}
        otpTimer={otpTimer}
        mobileFieldError={errors[getMobileErrorKey()] || errors[getOtpErrorKey()]}
        onSendMobileOtp={handleSendOtp}
        onVerifyMobileOtp={handleVerifyOtp}
        onResendMobileOtp={handleResendOtp}
      />
    </div>
  );
};

export default Register;
